import { useTranslation } from "react-i18next";
import { SELF_MERGE_VALUES, type MergeRule, type SelfMerge } from "../../../api/settingsApi";
import { systemGroupName } from "../../../features/access";
import Field from "../../../ui/forms/Field";
import FieldGroup from "../../../ui/forms/FieldGroup";
import { FORM_COMPACT_CONTROL_CLASS } from "../../../ui/styles/formStyles";

interface SecondCuratorRuleProps {
  /** The own-change rule of tools and skills with code. */
  rule: MergeRule;
  /** Omitted: shown read-only. */
  onChange?: (rule: MergeRule) => void;
}

/**
 * "Tools and skills with code need a second curator" (ADR-0028, the one enforced row). The
 * stored rule has a value for authors in the project's Administrators and one for every other
 * author: one choice sets both while they agree, and each shows on its own only when they
 * differ (revision 2's PV-1). Unequal values are never merged silently.
 */
export default function SecondCuratorRule({ rule, onChange }: SecondCuratorRuleProps) {
  const { t } = useTranslation();
  const name = t("projects.settings.rules.secondCurator");
  if (rule.contributor === rule.projectAdministrator) {
    return <Choice label={name} value={rule.contributor} onChange={onChange && ((value) => { onChange({ contributor: value, projectAdministrator: value }); })} />;
  }
  return (
    <FieldGroup legend={name} className="space-y-1">
      <Choice label={systemGroupName(t, "project_administrators")} value={rule.projectAdministrator}
        onChange={onChange && ((value) => { onChange({ ...rule, projectAdministrator: value }); })} />
      <Choice label={t("projects.settings.rules.otherAuthors")} value={rule.contributor}
        onChange={onChange && ((value) => { onChange({ ...rule, contributor: value }); })} />
    </FieldGroup>
  );
}

function Choice({ label, value, onChange }: { label: string; value: SelfMerge; onChange: ((value: SelfMerge) => void) | undefined }) {
  const { t } = useTranslation();
  const word = (choice: SelfMerge) => t(`projects.settings.secondCurator.${choice}`);
  return (
    <Field label={label} layout="inline">
      {(control) =>
        onChange === undefined ? (
          <span id={control.id} className="font-semibold">{word(value)}</span>
        ) : (
          <select {...control} className={`${FORM_COMPACT_CONTROL_CLASS} max-w-xs`} value={value}
            onChange={(event) => { onChange(event.target.value as SelfMerge); }}>
            {SELF_MERGE_VALUES.map((choice) => <option key={choice} value={choice}>{word(choice)}</option>)}
          </select>
        )
      }
    </Field>
  );
}
